import React, { useState } from 'react';
import { AppIcon } from '../../icons/appIcons.tsx';

interface AddFamilyFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface FamilyFormData {
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  secondaryContactFirstName: string;
  secondaryContactLastName: string;
  secondaryContactEmail: string;
  secondaryContactPhone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  monthlyFee: string;
  membershipStatus: string;
  initialPassword: string;
}

export default function AddFamilyForm({ onClose, onSuccess }: AddFamilyFormProps) {
  const [formData, setFormData] = useState<FamilyFormData>({
    familyName: '',
    primaryContactFirstName: '',
    primaryContactLastName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    secondaryContactFirstName: '',
    secondaryContactLastName: '',
    secondaryContactEmail: '',
    secondaryContactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    monthlyFee: '150.00',
    membershipStatus: 'active',
    initialPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [createdUser, setCreatedUser] = useState<any>(null);

  const handleInputChange = (field: keyof FamilyFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          monthlyFee: parseFloat(formData.monthlyFee),
          initialPassword: formData.initialPassword.trim() || undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setCreatedUser(data.user);
        // Don't call onSuccess immediately - show the credentials first
      } else {
        setErrors(data.errors?.map((err: any) => err.msg) || [data.message]);
      }
    } catch (error) {
      console.error('Error creating family:', error);
      setErrors(['Failed to create family. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      familyName: '',
      primaryContactFirstName: '',
      primaryContactLastName: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      secondaryContactFirstName: '',
      secondaryContactLastName: '',
      secondaryContactEmail: '',
      secondaryContactPhone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      monthlyFee: '150.00',
      membershipStatus: 'active',
      initialPassword: ''
    });
    setCreatedUser(null);
    setErrors([]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-4xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {createdUser ? 'Family Created Successfully!' : 'Add New Family'}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-red-800 font-medium mb-2">Please fix the following errors:</h4>
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {createdUser && (
              <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-green-800 font-medium mb-4 flex items-center">
                  Family and User Account Created Successfully!
                </h4>
                <div className="bg-white p-4 rounded border border-green-300">
                  <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <AppIcon name="lock" className="w-4 h-4" />
                    Login Credentials for Primary Contact:
                  </h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-mono bg-gray-100 px-2 py-1 rounded">{createdUser.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Password:</span>
                      <span className="font-mono bg-yellow-100 px-2 py-1 rounded font-bold">
                        {createdUser.temporaryPassword}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-yellow-800 text-sm">
                      {createdUser.passwordWasGenerated ? (
                        <>
                          <strong>Important:</strong> A random password was generated because none was set on the form.
                          Share these credentials with the family.
                        </>
                      ) : (
                        <>The password you entered is active. The family can log in with the email and password above.</>
                      )}
                    </p>
                  </div>
                  <div className="mt-3 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(`Email: ${createdUser.email}\nPassword: ${createdUser.temporaryPassword}`);
                        alert('Login credentials copied to clipboard!');
                      }}
                      className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 inline-flex items-center gap-1.5"
                    >
                      <AppIcon name="clipboard" className="w-4 h-4" />
                      Copy Credentials
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!createdUser && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Family Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Family Information</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Family Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.familyName}
                        onChange={(e) => handleInputChange('familyName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="The Smith Family"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monthly Fee <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          value={formData.monthlyFee}
                          onChange={(e) => handleInputChange('monthlyFee', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          value={formData.membershipStatus}
                          onChange={(e) => handleInputChange('membershipStatus', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        >
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Primary Contact */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Primary Contact (Will Receive Login)</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.primaryContactFirstName}
                          onChange={(e) => handleInputChange('primaryContactFirstName', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.primaryContactLastName}
                          onChange={(e) => handleInputChange('primaryContactLastName', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email (login) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.primaryContactEmail}
                        onChange={(e) => handleInputChange('primaryContactEmail', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="family@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Login password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        minLength={6}
                        value={formData.initialPassword}
                        onChange={(e) => handleInputChange('initialPassword', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="At least 6 characters"
                        autoComplete="new-password"
                      />
                      <p className="text-xs text-gray-500 mt-1">This is the password they will use to sign in (not auto-generated).</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.primaryContactPhone}
                        onChange={(e) => handleInputChange('primaryContactPhone', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Secondary Contact */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Secondary Contact (Optional)</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                        <input
                          type="text"
                          value={formData.secondaryContactFirstName}
                          onChange={(e) => handleInputChange('secondaryContactFirstName', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                        <input
                          type="text"
                          value={formData.secondaryContactLastName}
                          onChange={(e) => handleInputChange('secondaryContactLastName', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={formData.secondaryContactEmail}
                        onChange={(e) => handleInputChange('secondaryContactEmail', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={formData.secondaryContactPhone}
                        onChange={(e) => handleInputChange('secondaryContactPhone', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="123 Main Street"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                          placeholder="TX"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              {createdUser ? (
                <>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Create Another Family
                  </button>
                  <button
                    type="button"
                    onClick={onSuccess}
                    className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90"
                  >
                    Close & Refresh List
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Family & User Account'}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 